/**
 * Spec №14.1.1-D (D-DS5) — the declared-view toolbar. Search + filter chips over
 * a declared list, rendered ENGINE-SIDE (from `DeclaredListView`) so every Place
 * inherits it with zero per-view code. All state lives in the URL (shareable,
 * back-button-safe): `q` = search, `sort` = `field:dir`, and one param per active
 * field filter. Search + option/status/reference filters push to the server via
 * the №11.1 bounded-read `list_*` (see `src/engine/entities.ts`); nothing is
 * fetched-everything client-side.
 *
 * Styling is 100% the canonical `app-chrome` toolbar-list component (R1):
 * `.list-toolbar` / `.list-toolbar__search` / `.chip` / `.rowmenu`. No new CSS.
 */
import { useEffect, useRef, useState } from "react";
import { useEntities, useEntityType } from "../../../lib/hooks/use-kuvio";
import { entityDisplayName, type FilterSpec } from "../../lib/declared-view";

const SEARCH_DEBOUNCE_MS = 250;

export function ListToolbar({
  entityName,
  filters,
  search,
  activeFilters,
  onSearch,
  onFilter,
}: {
  entityName: string;
  filters: FilterSpec[];
  search: string;
  /** field → current value (id for references, value for enum/status). */
  activeFilters: Record<string, string>;
  onSearch: (value: string) => void;
  onFilter: (field: string, value: string | null) => void;
}) {
  // Local echo of the search box; the URL is the source of truth but writing it
  // on every keystroke would spam history — debounce, and mirror external
  // changes (back button, Clear) back into the box. Syncing the prop into local
  // state is done during render (not in an effect) per React's derived-state rule.
  const [text, setText] = useState(search);
  const [lastSearch, setLastSearch] = useState(search);
  if (lastSearch !== search) {
    setLastSearch(search);
    setText(search);
  }
  useEffect(() => {
    if (text === search) return;
    const t = setTimeout(() => onSearch(text), SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(t);
  }, [text, search, onSearch]);

  return (
    <div className="list-toolbar">
      <div className="list-toolbar__search">
        <svg className="g" viewBox="0 0 16 16" fill="none" aria-hidden="true">
          <circle cx="7" cy="7" r="4.5" stroke="currentColor" strokeWidth="1.4" />
          <path d="M11 11l3 3" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
        </svg>
        <input
          value={text}
          onChange={(e) => setText(e.target.value)}
          placeholder={`Search ${entityName.toLowerCase()}…`}
          aria-label={`Search ${entityName.toLowerCase()}`}
        />
        {text && (
          <button
            type="button"
            className="rowmenu-btn"
            aria-label="Clear search"
            onClick={() => { setText(""); onSearch(""); }}
          >
            ✕
          </button>
        )}
      </div>
      {filters.map((spec) => (
        <FilterChip
          key={spec.field}
          spec={spec}
          value={activeFilters[spec.field] ?? null}
          onChange={(v) => onFilter(spec.field, v)}
        />
      ))}
    </div>
  );
}

/** One filter chip: closed shows "Label ▾"; active shows "Label: value ✕". The
 *  option list opens in the canonical `.rowmenu` popover. Reference options are
 *  loaded lazily (only when the chip is opened or already active). */
function FilterChip({
  spec,
  value,
  onChange,
}: {
  spec: FilterSpec;
  value: string | null;
  onChange: (value: string | null) => void;
}) {
  const [pos, setPos] = useState<{ top: number; left: number } | null>(null);
  const btnRef = useRef<HTMLButtonElement>(null);
  const active = value != null && value !== "";
  const wantTargets = spec.kind === "reference" && (pos != null || active);

  // Reference filters resolve their options from the target type's rows. The
  // type itself is already in the global types cache (no fetch); the rows are
  // fetched lazily and server-capped.
  const targetType = useEntityType(spec.kind === "reference" ? spec.target : undefined);
  const targetRows = useEntities(wantTargets ? targetType.data : undefined);

  const options: { value: string; label: string }[] =
    spec.kind === "reference"
      ? (targetRows.data?.entities ?? []).map((e) => ({
          value: String(e.id),
          label: targetType.data ? entityDisplayName(e as Record<string, unknown>, targetType.data) : String(e.id),
        }))
      : (spec.choices ?? []).map((c) => ({ value: c, label: c }));

  const activeLabel = active
    ? (options.find((o) => o.value === value)?.label ?? value)
    : null;

  function toggle() {
    if (pos) { setPos(null); return; }
    const r = btnRef.current?.getBoundingClientRect();
    if (r) setPos({ top: r.bottom + 4, left: r.left });
  }

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        className={`chip${active ? " is-on" : ""}`}
        aria-haspopup="menu"
        aria-expanded={pos != null}
        onClick={toggle}
      >
        {active ? (
          <>
            <span>{spec.label}: {activeLabel}</span>
            <span
              role="button"
              aria-label={`Clear ${spec.label.toLowerCase()} filter`}
              onClick={(e) => { e.stopPropagation(); onChange(null); setPos(null); }}
            >
              ✕
            </span>
          </>
        ) : (
          <span>{spec.label} ▾</span>
        )}
      </button>
      {pos && (
        <>
          <div className="rowmenu__scrim" onClick={() => setPos(null)} />
          <div className="rowmenu" role="menu" style={{ top: pos.top, left: pos.left }}>
            {spec.kind === "reference" && targetRows.isLoading && (
              <button type="button" role="menuitem" disabled>Loading…</button>
            )}
            {!targetRows.isLoading && options.length === 0 && (
              <button type="button" role="menuitem" disabled>No options</button>
            )}
            {options.map((o) => (
              <button
                key={o.value}
                type="button"
                role="menuitemradio"
                aria-checked={o.value === value}
                onClick={() => { onChange(o.value === value ? null : o.value); setPos(null); }}
              >
                {o.value === value ? "✓ " : ""}{o.label}
              </button>
            ))}
          </div>
        </>
      )}
    </>
  );
}
